import {
  useState, useEffect, useCallback, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { EMPTY_TARGETS } from '../../../components/ui/AssignmentTargetPicker';

const WINNERS_VISIBILITY = ['winners_only', 'participants', 'scope'];

// AssignmentTargetPicker'ın obje şekli → BE'nin düz kapsam alanları (RaffleRequest).
// Çekilişte ünvan (titleIds) kırılımı YOK — BE'nin RaffleRequest'i de taşımaz;
// picker'dan gelse bile sessizce düşürülür (sözleşme tek yerde: burada).
export function toRafflePayload(form, questions) {
  const t = form.targets || EMPTY_TARGETS;
  const ex = t.excludes || {};
  return {
    title: (form.title || '').trim(),
    description: form.description || undefined,
    imageUrl: form.imageUrl || undefined,
    entryDeadline: form.entryDeadline,
    winnerCount: Number(form.winnerCount) || 1,
    itemsPerWinner: Number(form.itemsPerWinner) || 1,
    backupWinnerCount: Number(form.backupWinnerCount) || 0,
    claimDeadlineDays: form.claimDeadlineDays === '' || form.claimDeadlineDays == null
      ? undefined
      : Number(form.claimDeadlineDays),
    autoDraw: !!form.autoDraw,
    winnersVisibility: form.winnersVisibility || 'winners_only',
    userIds: t.userIds || [],
    departmentIds: t.departmentIds || [],
    locationIds: t.locationIds || [],
    userGroupIds: t.userGroupIds || [],
    subTenantIds: t.subTenantIds || [],
    includeAll: !!t.includeAll,
    excludes: {
      userIds: ex.userIds || [],
      departmentIds: ex.departmentIds || [],
      locationIds: ex.locationIds || [],
      userGroupIds: ex.userGroupIds || [],
      subTenantIds: ex.subTenantIds || [],
    },
    questions: (questions || [])
      .filter((q) => (q.text || '').trim())
      .map((q, i) => ({
        id: q.id || undefined,
        text: q.text.trim(),
        isRequired: !!q.isRequired,
        sortOrder: i,
      })),
  };
}

const useRaffleApi = (queryParams) => {
  const { t } = useTranslation();

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const lastValidQueryParams = useRef(null);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0
      ? queryParams
      : lastValidQueryParams.current || { pageNumber: 1, pageSize: 10 };
    lastValidQueryParams.current = params;
    setFetching(true);
    api.get(`/v1/raffles?${new URLSearchParams(params)}`)
      .then((res) => {
        setData(res.data?.data?.data || []);
        setPagination({
          totalRowCount: res.data?.data?.totalRowCount || 0,
          pageSize: res.data?.data?.pageSize || 10,
          pageIndex: (res.data?.data?.pageIndex || 1) - 1,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const reload = useCallback(() => setRefreshTrigger((v) => v + 1), []);

  const create = useCallback(async (payload) => {
    try {
      await api.post('/v1/raffles', payload);
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const update = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/raffles/${id}`, payload);
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const remove = useCallback(async (ids) => {
    try {
      await Promise.all(ids.map((id) => api.delete(`/v1/raffles/${id}`)));
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const publish = useCallback(async (id) => {
    try {
      await api.post(`/v1/raffles/${id}/publish`);
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const cancel = useCallback(async (id) => {
    try {
      await api.post(`/v1/raffles/${id}/cancel`);
      reload();
      return true;
    } catch { return false; }
  }, [reload]);

  const visibilityOptions = useMemo(
    () => WINNERS_VISIBILITY.map((v) => ({ id: v, name: t(`raffle.visibility.${v}`) })),
    [t],
  );

  const objectSchema = useMemo(() => [
    { name: 'title', label: t('raffle.fields.title'), type: 'text', grid: 12, required: true },
    { name: 'description', label: t('raffle.fields.description'), type: 'textarea', grid: 12 },
    {
      name: 'imageUrl',
      label: t('raffle.fields.image'),
      type: 'pictureUpload',
      grid: 12,
      accept: 'image/*',
      validTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
      maxSize: 5,
      purpose: 'raffleImage',
    },
    {
      name: 'entryDeadline', label: t('raffle.fields.entryDeadline'), type: 'date', grid: 6, required: true,
      description: t('raffle.fields.entryDeadlineHint'),
    },
    { name: 'winnerCount', label: t('raffle.fields.winnerCount'), type: 'number', grid: 6, required: true },
    { name: 'itemsPerWinner', label: t('raffle.fields.itemsPerWinner'), type: 'number', grid: 6, required: true },
    {
      name: 'backupWinnerCount', label: t('raffle.fields.backupWinnerCount'), type: 'number', grid: 6,
      description: t('raffle.fields.backupWinnerCountHint'),
    },
    {
      name: 'claimDeadlineDays', label: t('raffle.fields.claimDeadlineDays'), type: 'number', grid: 6,
      description: t('raffle.fields.claimDeadlineDaysHint'),
    },
    {
      name: 'winnersVisibility', label: t('raffle.fields.winnersVisibility'), type: 'select', grid: 6,
      options: visibilityOptions,
    },
    {
      name: 'autoDraw', label: t('raffle.fields.autoDraw'), type: 'checkbox', grid: 12,
      description: t('raffle.fields.autoDrawHint'),
    },
    { name: 'targets', label: t('raffle.fields.scope'), type: 'assignmentTargets', grid: 12 },
  ], [t, visibilityOptions]);

  const columns = useMemo(() => ([
    { name: 'title', label: t('raffle.fields.title'), options: { filter: false, sort: true } },
    {
      name: 'status',
      label: t('raffle.fields.status'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => t(`raffle.status.${v}`, { defaultValue: v }),
      },
    },
    {
      name: 'entryDeadline',
      label: t('raffle.fields.entryDeadline'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (v ? new Date(v).toLocaleDateString() : '-'),
      },
    },
    { name: 'entryCount', label: t('raffle.fields.entryCount'), options: { filter: false, sort: true } },
    { name: 'winnerCount', label: t('raffle.fields.winnerCount'), options: { filter: false, sort: false } },
    {
      name: 'drawnAt',
      label: t('raffle.fields.drawnAt'),
      options: {
        filter: false, sort: true,
        customBodyRender: (v) => (v ? new Date(v).toLocaleString() : '-'),
      },
    },
  ]), [t]);

  return {
    fetching, data, pagination, columns, objectSchema, reload,
    create, update, remove, publish, cancel,
  };
};

export default useRaffleApi;

// ── Doğrudan API (detay sayfası) ──
export const getRaffle = (id) => api.get(`/v1/raffles/${id}`);
export const getRaffleEntries = (id) => api.get(`/v1/raffles/${id}/entries`);
export const drawRaffle = (id) => api.post(`/v1/raffles/${id}/draw`);
export const publishRaffle = (id) => api.post(`/v1/raffles/${id}/publish`);
export const cancelRaffle = (id) => api.post(`/v1/raffles/${id}/cancel`);
export const exportRaffleWinners = (id) => api.get(`/v1/raffles/${id}/winners/export`, { responseType: 'blob' });

// ── Self-servis ──
export const getMyRaffles = () => api.get('/v1/raffles/my');
export const getMyRaffle = (id) => api.get(`/v1/raffles/my/${id}`);
export const enterRaffle = (id, payload) => api.post(`/v1/raffles/my/${id}/enter`, payload);
export const claimRaffle = (id, decision) => api.post(`/v1/raffles/my/${id}/claim`, { decision });
